import { useState } from 'react'
import type { SavedSummary } from '@creanizer/types'
import { Button } from '@creanizer/ui'
import { Card } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Trash2, FileText, Search, Folder } from 'lucide-react'

interface SavedSummariesListProps {
  summaries: SavedSummary[]
  onLoad: (summary: SavedSummary) => void
  onDelete: (id: string) => void
}

export function SavedSummariesList({ summaries, onLoad, onDelete }: SavedSummariesListProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null)
  
  // フィルタリング
  const filteredSummaries = summaries.filter(summary => {
    if (!summary) return false
    
    const matchesSearch = searchQuery === '' || 
      summary.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      summary.result.summary.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesFolder = !selectedFolder || summary.folderId === selectedFolder
    
    return matchesSearch && matchesFolder
  })
  
  // フォルダ一覧取得（仮実装）
  const folders = Array.from(new Set(summaries.map(s => s?.folderId).filter(Boolean)))
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  
  const getPreview = (summary: SavedSummary) => {
    const text = summary.result.summary
    return text.length > 100 ? text.slice(0, 100) + '...' : text
  }
  
  if (summaries.length === 0) {
    return (
      <Card className="p-8 text-center">
        <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <p className="text-muted-foreground">保存済みの要約はありません</p>
      </Card>
    )
  }
  
  return (
    <div className="space-y-4">
      {/* 検索とフィルター */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="要約を検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        {folders.length > 0 && (
          <select
            value={selectedFolder || ''}
            onChange={(e) => setSelectedFolder(e.target.value || null)}
            className="px-3 py-2 border rounded-md"
          >
            <option value="">すべてのフォルダ</option>
            {folders.map(folder => (
              <option key={folder} value={folder}>
                {folder}
              </option>
            ))}
          </select>
        )}
      </div>
      
      {/* 要約リスト */}
      <div className="space-y-3">
        {filteredSummaries.length === 0 ? (
          <Card className="p-4 text-center text-muted-foreground">
            検索条件に一致する要約が見つかりません
          </Card>
        ) : (
          filteredSummaries.map(summary => (
            <Card key={summary.id} className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <h4 className="font-medium">{summary.title}</h4>
                  </div>
                  
                  <p className="text-sm text-muted-foreground">
                    {getPreview(summary)}
                  </p>
                  
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>元: {summary.result.metadata.originalLength.toLocaleString()}文字</span>
                    <span>→</span>
                    <span>要約: {summary.result.metadata.summaryLength.toLocaleString()}文字</span>
                    <span>({Math.round(summary.result.metadata.compressionRatio * 100)}%)</span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>{formatDate(summary.createdAt)}</span>
                    {summary.tags && summary.tags.length > 0 && (
                      <div className="flex gap-1">
                        {summary.tags.map(tag => (
                          <span key={tag} className="px-2 py-1 bg-muted rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {summary.memo && (
                    <p className="text-xs text-muted-foreground italic">
                      メモ: {summary.memo}
                    </p>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onLoad(summary)}
                  >
                    読み込み
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (confirm('この要約を削除しますか？')) {
                        onDelete(summary.id)
                      }
                    }}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  )
}